import { useDocumentsWithBacklinks } from '@/api';
import type { DocumentEntity } from '@/api/types/document';
import { Stack, Typography } from '@mui/material';
import type { Accept, FileRejection } from 'react-dropzone';
import React, { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { FileExtension, type FileExtensionProps } from '@shared/ui';
import { FileUploadAttachmentList } from './FileUploadAttachmentList';
import { FileUploadLibrarySearch } from './FileUploadLibrarySearch';
import { FileUploadShell } from './FileUploadShell';
import type {
  FileUploadDocumentRowMeta,
  FileUploadFileRowMeta,
  FileUploadLibraryOption,
  FileUploadRejectionLabels,
} from './types';

export type {
  FileUploadLibraryOption,
  FileUploadRejectionLabels,
  FileUploadUploadedItem,
} from './types';
export { FileUploadShell } from './FileUploadShell';
export { FileUploadLibrarySearch } from './FileUploadLibrarySearch';
export { FileUploadAttachmentList } from './FileUploadAttachmentList';
export { FileUploadUploadedChips } from './FileUploadUploadedChips';

type LibraryRenderOption = (
  props: React.HTMLAttributes<HTMLLIElement> & { key?: string },
  option: FileUploadLibraryOption,
  /** Set when the row is offered but not selectable — see `libraryOptionUnsupported`. */
  unsupportedReason?: string,
) => React.ReactNode;

export interface FileUploadProps {
  /** Passed to react-dropzone's `accept` attribute mapping. */
  accept?: Accept;
  /** Maximum files per drop / browse action. */
  maxFiles?: number;
  /**
   * Copy for a pick the dropzone refused — see {@link FileUploadRejectionLabels}.
   *
   * Omitting it does not make the refusal silent: the shell falls back to naming the
   * refused files. Pass it to say WHY, which is the half that tells the auditor what
   * to do about it.
   */
  rejectionLabels?: FileUploadRejectionLabels;
  /** Render the refusal yourself; suppresses the shell's inline message. */
  onFilesRejected?: (rejections: readonly FileRejection[]) => void;
  /** Disables dropzone, library picker, and row remove controls. */
  disabled?: boolean;
  /** Allow selecting multiple library documents when the library is enabled. */
  multiple?: boolean;

  /** Controlled selected files from disk. */
  files?: File[];
  fileExtensionSize?: FileExtensionProps['size'];
  /** Controlled selected library rows. */
  documents?: FileUploadLibraryOption[];
  /** Optional explicit ids to exclude from the library picker. */
  selectedDocumentIds?: number[];
  onFilesSelected?: (files: File[]) => void;
  onRemoveFile?: (index: number) => void;
  onRemoveDocument?: (documentId: number) => void;
  onDocumentsSelected?: (documents: FileUploadLibraryOption[]) => void;
  /** Where a *document* row links to; the row opens it in a new tab. */
  getDocumentHref?: (documentId: number) => string | undefined;
  /** Enable inline rename (hover pencil) for saved document rows in the attachment list. */
  enableDocumentRename?: boolean;
  /** See `FileUploadAttachmentList.onDocumentRenamed`: keeps a snapshot's names current. */
  onDocumentRenamed?: (documentId: number, name: string) => void;
  /** Trailing actions (e.g. a kebab menu) rendered on each *document* row. */
  renderDocumentActions?: (doc: FileUploadLibraryOption) => React.ReactNode;
  /** A tag drawn after each *document* row's name (`DocumentFileRow.titleAdornment`). */
  renderDocumentTitleAdornment?: (doc: FileUploadLibraryOption) => React.ReactNode;
  /** The same tag on a library picker row, after its name (the default row renderer only). */
  libraryOptionAdornment?: (option: FileUploadLibraryOption) => React.ReactNode;
  /** Show the "Uploaded by" line on document rows. Default `true`. */
  showUploader?: boolean;

  /** Per-file row state aligned with `files` by index. */
  filesMeta?: FileUploadFileRowMeta[];
  /** Per-document row state keyed by `id`. */
  documentsMetaById?: Record<number, FileUploadDocumentRowMeta>;

  /** Primary line before the browse link (drag-and-drop hint). */
  uploadLeadingHint?: string;
  /** Inline browse control label. */
  browseLabel?: string;
  /** Caption under the primary line (e.g. supported formats). */
  supportedFormatsText?: string;
  /** Library autocomplete placeholder. */
  searchPlaceholder?: string;
  /** Heading shown above the attachment list when at least one row is visible. */
  attachmentsHeading?: string;
  /** Optional content rendered between the upload shell and attachment list. */
  beforeAttachments?: React.ReactNode;

  /**
   * When true, the library search row is rendered under the dashed drop zone.
   * If `libraryOptions` is not provided, options are fetched via
   * `useDocumentsWithBacklinks` using `useParams().id`.
   */
  showLibrarySearch?: boolean;
  /** Inject options when you must bypass the audit `useDocumentsWithBacklinks` fetch. */
  libraryOptions?: FileUploadLibraryOption[];
  /** Loading flag for injected `libraryOptions`. */
  libraryLoading?: boolean;
  /** Autocomplete `noOptionsText`. */
  libraryNoOptionsText?: string;
  /** Custom row renderer for library options. */
  renderLibraryOption?: LibraryRenderOption;

  /**
   * Hide options already present in `documents` from the library picker. Default `true`.
   * Set to `false` to allow re-selecting the same id (pre-existing behaviour).
   */
  filterSelectedLibraryOptions?: boolean;

  /**
   * Restrict WHICH library rows the picker offers, on top of the always-applied
   * "not deleted" and "not already selected" rules.
   *
   * For rows that do not BELONG in this picker at all. A row the caller's endpoint
   * would refuse for its FORMAT is a different case and belongs to
   * `libraryOptionUnsupported`: filtering that one leaves the auditor hunting for a
   * document that is sitting in the library, present and unmentioned.
   */
  libraryOptionFilter?: (option: FileUploadLibraryOption) => boolean;

  /**
   * Rows the picker LISTS AND REFUSES: return the reason the row cannot be taken,
   * or `undefined` when it can.
   *
   * One function drives all three halves of that answer — the row sorts under
   * `libraryUnsupportedHeading`, renders disabled with the reason beneath its name,
   * and is rejected if a pick ever reaches the change handler anyway — so the
   * heading, the dimming and the sentence cannot drift apart.
   *
   * The alternative is `libraryOptionFilter`, and it is the wrong tool here: a file
   * that vanishes from the picker is indistinguishable from one that was never
   * uploaded, so the auditor searches for it instead of reading why it is refused.
   */
  libraryOptionUnsupported?: (option: FileUploadLibraryOption) => string | undefined;
  /** Heading over the refused rows. Only rendered when there is one to head. */
  libraryUnsupportedHeading?: string;

  /**
   * When true the library picker behaves as an "add another" field:
   *   - its internal value is always empty (selected rows only appear in the attachment list),
   *   - every pick calls `onLibraryOptionAdded(option)` (for attach-by-id flows),
   *   - `onDocumentsSelected` still receives the full next list (controlled contract).
   */
  additiveLibraryPick?: boolean;
  onLibraryOptionAdded?: (option: FileUploadLibraryOption) => void;

  /**
   * `data-testid` on the dashed shell root. Defaults to `file-upload-shell`.
   * Use `file-upload-drop-zone` when matching legacy request-block tests.
   */
  shellDataTestId?: string;
}

const defaultRenderLibraryOption = (
  props: Parameters<LibraryRenderOption>[0],
  option: FileUploadLibraryOption,
  unsupportedReason?: string,
  adornment?: React.ReactNode,
) => (
  <Stack component='li' direction='row' alignItems='center' spacing={2} {...props} key={option.id}>
    <FileExtension document={option.document} />
    <Stack spacing={0.25} sx={{ minWidth: 0 }}>
      <Stack direction='row' alignItems='center' spacing={1} sx={{ minWidth: 0 }}>
        <Typography variant='body2' fontWeight={500}>
          {option.name}
        </Typography>
        {adornment}
      </Stack>
      {/* THE REASON RIDES THE ROW, not a tooltip: a refused row that only dims makes
          the auditor guess, and the sentence is the half that names the way out. */}
      {unsupportedReason ? (
        <Typography variant='caption' color='text.secondary'>
          {unsupportedReason}
        </Typography>
      ) : null}
    </Stack>
  </Stack>
);

const FileUpload: React.FC<FileUploadProps> = ({
  accept,
  maxFiles = 1,
  rejectionLabels,
  onFilesRejected,
  disabled = false,
  multiple = false,

  files = [],
  fileExtensionSize = 'middle',
  documents = [],
  selectedDocumentIds,
  onFilesSelected,
  onRemoveFile,
  onRemoveDocument,
  onDocumentsSelected,
  getDocumentHref,
  enableDocumentRename = false,
  onDocumentRenamed,
  renderDocumentActions,
  renderDocumentTitleAdornment,
  libraryOptionAdornment,
  showUploader = true,

  filesMeta,
  documentsMetaById,

  uploadLeadingHint = 'Drag & drop your files here',
  browseLabel = 'Browse',
  supportedFormatsText = '',
  searchPlaceholder = 'Search files',
  attachmentsHeading,
  beforeAttachments,

  showLibrarySearch = false,
  libraryOptions: libraryOptionsProp,
  libraryLoading: libraryLoadingProp,
  libraryNoOptionsText,
  renderLibraryOption,

  filterSelectedLibraryOptions = true,
  libraryOptionFilter,
  libraryOptionUnsupported,
  libraryUnsupportedHeading = 'Not supported',
  additiveLibraryPick = false,
  onLibraryOptionAdded,

  shellDataTestId = 'file-upload-shell',
}) => {
  const { id: auditId } = useParams<{ id: string }>();
  const shouldFetchAuditDocs = showLibrarySearch && !libraryOptionsProp;

  const { data: fetchedAuditDocs = [], isLoading: auditDocsLoading } = useDocumentsWithBacklinks(
    auditId ?? '',
    undefined,
    { enabled: shouldFetchAuditDocs },
  );

  const libraryOptions = useMemo<FileUploadLibraryOption[]>(() => {
    if (libraryOptionsProp) return libraryOptionsProp;
    return (fetchedAuditDocs as DocumentEntity[]).filter(
      (doc) => !doc.deleted,
    ) as FileUploadLibraryOption[];
  }, [fetchedAuditDocs, libraryOptionsProp]);

  const libraryLoading = libraryOptionsProp ? Boolean(libraryLoadingProp) : auditDocsLoading;

  const visibleLibraryOptions = useMemo(() => {
    const offered = libraryOptionFilter
      ? libraryOptions.filter(libraryOptionFilter)
      : libraryOptions;

    const idsToFilter =
      selectedDocumentIds && selectedDocumentIds.length > 0
        ? selectedDocumentIds
        : documents.map((d) => d.id);

    if (!filterSelectedLibraryOptions || idsToFilter.length === 0) return offered;

    const usedIds = new Set(idsToFilter);
    return offered.filter((o) => !usedIds.has(o.id));
  }, [
    libraryOptions,
    libraryOptionFilter,
    documents,
    filterSelectedLibraryOptions,
    selectedDocumentIds,
  ]);

  /**
   * REFUSED ROWS SORT LAST, and it is not decoration: MUI groups by ADJACENCY, so one
   * unsupported row sitting between two supported ones prints its heading twice and
   * scatters the refused half through the list it is meant to be separated from.
   */
  const orderedLibraryOptions = useMemo(() => {
    if (!libraryOptionUnsupported) return visibleLibraryOptions;

    const supported = visibleLibraryOptions.filter((option) => !libraryOptionUnsupported(option));
    if (supported.length === visibleLibraryOptions.length) return visibleLibraryOptions;

    return [
      ...supported,
      ...visibleLibraryOptions.filter((option) => Boolean(libraryOptionUnsupported(option))),
    ];
  }, [visibleLibraryOptions, libraryOptionUnsupported]);

  const documentsWithBacklinks = useMemo(() => {
    if (!libraryOptions.length || !documents.length) return documents;

    const libraryOptionsById = new Map(libraryOptions.map((option) => [option.id, option]));

    return documents.map((document) => {
      const libraryOption = libraryOptionsById.get(document.id);
      if (!libraryOption) return document;

      const backlinks = document.backlinks?.length
        ? document.backlinks
        : (libraryOption.backlinks ?? document.backlinks);
      const addedBy = document.addedBy ?? libraryOption.addedBy;

      return backlinks !== document.backlinks || addedBy !== document.addedBy
        ? { ...document, backlinks, addedBy }
        : document;
    });
  }, [documents, libraryOptions]);

  const handleFilesFromDropzone = useCallback(
    (acceptedFiles: File[]) => {
      if (!onFilesSelected) return;
      onFilesSelected(multiple ? [...files, ...acceptedFiles] : acceptedFiles);
    },
    [files, multiple, onFilesSelected],
  );

  const handleRemoveFile = useCallback(
    (index: number) => {
      if (onRemoveFile) {
        onRemoveFile(index);
        return;
      }
      onFilesSelected?.(files.filter((_, i) => i !== index));
    },
    [files, onFilesSelected, onRemoveFile],
  );

  const handleRemoveDocument = useCallback(
    (documentId: number) => {
      if (onRemoveDocument) {
        onRemoveDocument(documentId);
        return;
      }
      onDocumentsSelected?.(documents.filter((doc) => doc.id !== documentId));
    },
    [documents, onDocumentsSelected, onRemoveDocument],
  );

  const handleLibraryChange = useCallback(
    (
      _event: React.SyntheticEvent,
      value: FileUploadLibraryOption | FileUploadLibraryOption[] | null,
    ) => {
      /* The dimming is the affordance; THIS is the rule. `getOptionDisabled` stops a
         click and a keyboard select, and that is every route MUI knows about — a
         refused row must not become a picked document by any of the ones it doesn't. */
      const refused = (option: FileUploadLibraryOption) =>
        Boolean(libraryOptionUnsupported?.(option));

      if (additiveLibraryPick) {
        const picked = Array.isArray(value) ? value[value.length - 1] : value;
        if (!picked || refused(picked)) return;
        if (documents.some((d) => d.id === picked.id)) return;
        onLibraryOptionAdded?.(picked);
        onDocumentsSelected?.([...documents, picked]);
        return;
      }
      /* Only an ADDITION can be refused. Filtering the emitted list instead would
         answer a mis-click on an Excel row by REMOVING the document already picked —
         the single-value picker replaces its value, so the refused half of that swap
         would leave an empty list behind. */
      const next = Array.isArray(value) ? value : value ? [value] : [];
      const added = next.filter((option) => !documents.some((doc) => doc.id === option.id));
      if (added.some(refused)) return;

      onDocumentsSelected?.(next);
    },
    [
      additiveLibraryPick,
      documents,
      libraryOptionUnsupported,
      onDocumentsSelected,
      onLibraryOptionAdded,
    ],
  );

  const libraryValue = useMemo<FileUploadLibraryOption | FileUploadLibraryOption[] | null>(() => {
    if (additiveLibraryPick) return multiple ? [] : null;
    return multiple ? documents : (documents[0] ?? null);
  }, [additiveLibraryPick, documents, multiple]);

  return (
    <Stack spacing={2}>
      <FileUploadShell
        rootDataTestId={shellDataTestId}
        uploadLeadingHint={uploadLeadingHint}
        browseLabel={browseLabel}
        supportedFormatsText={supportedFormatsText}
        disabled={disabled}
        onFilesSelected={onFilesSelected ? handleFilesFromDropzone : undefined}
        accept={accept}
        maxFiles={maxFiles}
        rejectionLabels={rejectionLabels}
        onFilesRejected={onFilesRejected}
        librarySection={
          showLibrarySearch ? (
            <FileUploadLibrarySearch
              options={orderedLibraryOptions}
              loading={libraryLoading}
              value={libraryValue}
              onChange={handleLibraryChange}
              multiple={multiple}
              disableCloseOnSelect={multiple && !additiveLibraryPick}
              placeholder={searchPlaceholder}
              disabled={disabled}
              noOptionsText={libraryNoOptionsText}
              getOptionDisabled={
                libraryOptionUnsupported
                  ? (option) => Boolean(libraryOptionUnsupported(option))
                  : undefined
              }
              groupBy={
                libraryOptionUnsupported
                  ? /* '' is the SUPPORTED half asking for no heading at all — the
                       refused rows are the exception being pointed at, and heading the
                       ordinary ones too would make the picker read as two lists. */
                    (option) => (libraryOptionUnsupported(option) ? libraryUnsupportedHeading : '')
                  : undefined
              }
              renderOption={(props, option) =>
                renderLibraryOption
                  ? renderLibraryOption(props, option, libraryOptionUnsupported?.(option))
                  : defaultRenderLibraryOption(
                      props,
                      option,
                      libraryOptionUnsupported?.(option),
                      libraryOptionAdornment?.(option),
                    )
              }
            />
          ) : undefined
        }
      />

      {beforeAttachments}

      {attachmentsHeading && (files.length > 0 || documents.length > 0) ? (
        <Typography variant='subtitle2' color='text.secondary' component='p' sx={{ m: 0 }}>
          {attachmentsHeading}
        </Typography>
      ) : null}

      <FileUploadAttachmentList
        files={files}
        filesMeta={filesMeta}
        documents={documentsWithBacklinks}
        fileExtensionSize={fileExtensionSize}
        metaByDocumentId={documentsMetaById}
        disabled={disabled}
        onRemoveFile={onFilesSelected || onRemoveFile ? handleRemoveFile : undefined}
        onRemoveDocument={
          onDocumentsSelected || onRemoveDocument ? handleRemoveDocument : undefined
        }
        getDocumentHref={getDocumentHref}
        enableDocumentRename={enableDocumentRename}
        onDocumentRenamed={onDocumentRenamed}
        renderDocumentActions={renderDocumentActions}
        renderDocumentTitleAdornment={renderDocumentTitleAdornment}
        showUploader={showUploader}
      />
    </Stack>
  );
};

export default FileUpload;
